import { useCallback, useEffect, useRef, useState } from 'react'
import Voice, { SpeechResultsEvent } from '@react-native-voice/voice'
import { Platform } from 'react-native'
import { check, openSettings, PERMISSIONS, request, RESULTS } from 'react-native-permissions'
import { useSnack } from './navigatorContext'

const MICROPHONE_PERMISSION = Platform.select({
  ios: PERMISSIONS.IOS.MICROPHONE,
  android: PERMISSIONS.ANDROID.RECORD_AUDIO,
})

export const useVoice = (onTranscript: (text: string) => void) => {
  const showSnack = useSnack()

  const [isListening, setIsListening] = useState(false)
  const [isAvailable, setIsAvailable] = useState(false)

  const lastSpokenText = useRef('')
  const allSpokenText = useRef('')

  const handleTranscript = useCallback(
    (event: SpeechResultsEvent) => {
      const spokenText = event.value?.join(' ') || ''

      // Voice returns cumulative text, so we need to extract only the new words
      const previousText = lastSpokenText.current
      const newText = spokenText.replace(previousText, '').trim()
      lastSpokenText.current = spokenText

      if (newText) {
        allSpokenText.current += newText + ' '
        onTranscript(newText)
      }
    },
    [onTranscript],
  )

  // On Android, most of the times the last word is missing on partial results
  // (probably because it stops listening automatically)
  // So this hack captures the final transcript and extracts the missing part
  const handleAndroidFinalTranscript = useCallback(
    (event: SpeechResultsEvent) => {
      const spokenText = event.value?.join(' ') || ''

      const finalText = spokenText.replace(allSpokenText.current, '').trim()
      if (finalText) {
        onTranscript(finalText)
        // We need to destroy here because on Android, the behavior is to stop listening automatically
        // But for some reason (probably a bug in the library), if we try to start listening again,
        // nothing happens unless we destroy the instance first
        Voice.destroy()
      }
    },
    [onTranscript],
  )

  useEffect(() => {
    const checkPermission = async () => {
      if (!MICROPHONE_PERMISSION) {
        setIsAvailable(false)
        return
      }

      try {
        const result = await check(MICROPHONE_PERMISSION)
        console.log('Microphone permission status on init:', result)
        const isUnavailable = result === RESULTS.UNAVAILABLE
        setIsAvailable(!isUnavailable)
      } catch {
        setIsAvailable(false)
      }
    }

    checkPermission()
  }, [])

  const bindVoiceListeners = useCallback(() => {
    Voice.onSpeechPartialResults = handleTranscript
    if (Platform.OS === 'android') {
      Voice.onSpeechResults = handleAndroidFinalTranscript
    }

    Voice.onSpeechEnd = () => {
      setIsListening(false)
    }
  }, [handleAndroidFinalTranscript, handleTranscript])

  useEffect(() => {
    bindVoiceListeners()

    return () => {
      Voice.destroy().then(Voice.removeAllListeners)
    }
  }, [bindVoiceListeners])

  const showPermissionSnack = () => {
    showSnack(
      'Microphone permission is required for voice input. You should enable this permission in your device settings.',
      null,
      'warning',
      undefined,
      {
        label: 'Open Settings',
        onPress: () => openSettings(),
      },
      'redesigned',
    )
  }

  const startListening = async () => {
    if (!MICROPHONE_PERMISSION) {
      return
    }

    const permission = await check(MICROPHONE_PERMISSION)

    if (permission === RESULTS.GRANTED || permission === RESULTS.LIMITED) {
      setIsListening(true)
      lastSpokenText.current = ''
      allSpokenText.current = ''
      bindVoiceListeners()
      await Voice.start('en-US', { EXTRA_MAX_RESULTS: 1 })
      return
    }

    if (permission === RESULTS.BLOCKED) {
      showPermissionSnack()
      return
    }

    // On android, if user denies permission multiple times, it gets blocked
    if (permission === RESULTS.DENIED) {
      const result = await request(MICROPHONE_PERMISSION)

      if (result === RESULTS.BLOCKED) {
        showPermissionSnack()
        return
      }
    }
  }

  const stopListening = async () => {
    setIsListening(false)
    lastSpokenText.current = ''
    allSpokenText.current = ''
    await Voice.destroy()
  }

  return { isListening, startListening, stopListening, isAvailable }
}
